'use client';

import { useState } from 'react';
import type { ChangeEvent } from 'react';
import { sanitizeDecimalInput } from './decimalInput';
import { formatPercent, parsePercent } from './percent';
import { useTouchedFields } from './useTouchedFields';
import { mapFieldValues } from './types';
import type { CalculatorField, CalculatorFieldValues } from './types';

/** Capped as it is typed, so a fourth digit is dropped outright rather than rounded in the way a cent would be. */
const MAXIMUM_FRACTION_DIGITS = 3;

export const usePercentFields = (initialValues: CalculatorFieldValues) => {
  const [values, setValues] = useState<CalculatorFieldValues>(initialValues);
  const { markTouched, isTouched, resetTouched } = useTouchedFields();

  const normalizeValue = (raw: string) => (raw === '' ? '' : formatPercent(parsePercent(raw)));

  const updateValue = (label: string, transform: (raw: string) => string) =>
    setValues(previous => ({ ...previous, [label]: transform(previous[label]) }));

  const handleChange = (label: string, value: string) => {
    markTouched(label);
    updateValue(label, () => sanitizeDecimalInput(value, MAXIMUM_FRACTION_DIGITS));
  };

  const handleBlur = (label: string) => {
    updateValue(label, normalizeValue);
  };

  /** What Calculate should read. Writes nothing, so the caller can validate before deciding whether to `commit`. */
  const normalize = (): CalculatorFieldValues => mapFieldValues(values, normalizeValue);

  const fieldProps = (field: CalculatorField, error?: string) => ({
    label: field.label,
    hint: field.hint,
    placeholder: field.placeholder,
    inputMode: 'decimal' as const,
    affix: { symbol: '%', position: 'suffix' } as const,
    value: values[field.label],
    error,
    invalid: Boolean(error),
    onChange: (event: ChangeEvent<HTMLInputElement>) => handleChange(field.label, event.target.value),
    onBlur: () => handleBlur(field.label),
  });

  return {
    values,
    normalize,
    commit: () => setValues(normalize()),
    reset: () => {
      resetTouched();
      setValues(initialValues);
    },
    isTouched,
    fieldProps,
  };
};

export type PercentFieldProps = ReturnType<typeof usePercentFields>['fieldProps'];
